import type { SavedJobState } from '../../../../__generated__/types.gqlgenerated';
import type { TagStatus } from '../../../../mads/Tag';

import { Tag, Box } from '../../../../mads';
import { useI18n } from '../../../../shared/I18n';

import { StyleSheet } from 'react-native';

export type ApplicationDraft =
    | {
          expiryTime: number;
      }
    | null
    | undefined;

export type ApplicationStatusBadgeProps = {
    savedJobState: SavedJobState | null | undefined;
    applicationDraft?: ApplicationDraft;
};

type ApplicationStatusConfig = {
    text: string;
    status: TagStatus;
};

/**
 * Determines if an application status badge should be displayed for a job
 * based on its GraphQL savedJobState and applicationDraft.
 *
 * SAVED + applicationDraft present = "Application started" (has draft, not just bookmarked)
 * SAVED + no applicationDraft = no badge (simple bookmark)
 */
export function hasApplicationStatusBadge(
    savedJobState: SavedJobState | null | undefined,
    applicationDraft?: ApplicationDraft,
): boolean {
    switch (savedJobState) {
        case 'APPLIED':
        case 'INTERVIEW':
        case 'INTERVIEWING':
        case 'OFFER':
        case 'OFFERED':
        case 'HIRED':
            return true;
        case 'SAVED':
            return applicationDraft != null;
        case 'UNSAVED':
        case 'REJECTED':
        case 'VISITED':
        default:
            return false;
    }
}

/**
 * Displays a status badge for job applications.
 *
 * Badge mapping (following iOS implementation):
 * - "Applied" - GraphQL APPLIED state
 * - "Application started" - GraphQL SAVED state with applicationDraft present
 * - "Interview" - INTERVIEW/INTERVIEWING states
 * - "Offer" - OFFER/OFFERED states
 * - "Hired" - HIRED state
 *
 * @example
 * ```tsx
 * <ApplicationStatusBadge
 *     savedJobState={job.savedJob?.state}
 *     applicationDraft={job.savedJob?.applicationDraft}
 * />
 * ```
 */
export function ApplicationStatusBadge({
    savedJobState,
    applicationDraft,
}: ApplicationStatusBadgeProps) {
    const i18n = useI18n();

    if (!hasApplicationStatusBadge(savedJobState, applicationDraft)) {
        return null;
    }

    let statusConfig: ApplicationStatusConfig;

    switch (savedJobState) {
        case 'APPLIED':
            statusConfig = {
                text: i18n.gettext('Applied'),
                status: 'info',
            };
            break;
        case 'SAVED':
            statusConfig = {
                text: i18n.gettext('Application started'),
                status: 'info',
            };
            break;
        case 'INTERVIEW':
        case 'INTERVIEWING':
            statusConfig = {
                text: i18n.gettext('Interview'),
                status: 'info',
            };
            break;
        case 'OFFER':
        case 'OFFERED':
            statusConfig = {
                text: i18n.gettext('Offer'),
                status: 'success',
            };
            break;
        case 'HIRED':
            statusConfig = {
                text: i18n.gettext('Hired'),
                status: 'success',
            };
            break;
        default:
            return null;
    }

    return (
        <Box sx={styles.tagContainer} accessible={true}>
            <Tag status={statusConfig.status} variant="light" sx={styles.tag}>
                {statusConfig.text}
            </Tag>
        </Box>
    );
}

const styles = StyleSheet.create({
    tagContainer: {
        alignSelf: 'flex-start',
        marginBottom: 2,
    },
    tag: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        minHeight: 24,
    },
});
